import { useEffect, useMemo, useState } from 'react';
import { useAuth, axiosClient } from '../../auth-lib';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayout, EmptyState } from '../../design-system';
import { buildNavChrome, canAccessNavKey, requiredNavKeyFor, applyNavBadges } from './navItems';
import { useNotifications } from '../../context/NotificationContext';
import NotificationPanel from '../../components/NotificationPanel';
import NotificationPermissionBanner from '../../components/NotificationPermissionBanner';
import AttendanceDayPrompt from '../../components/attendance/AttendanceDayPrompt';
import { useTallyNavStatus, tallyStatusDot } from '../../lib/tallyNavStatus';
import { useNavCounts } from '../../lib/navCounts';


function OrgAdminLayout({ children, title, subtitle, headerRight }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const location = useLocation();
  const chrome = useMemo(() => buildNavChrome(user), [user]);
  const { counts, countFor } = useNotifications();
  // Memoised on the counts object rather than on `chrome`, so a badge ticking
  // over doesn't rebuild the menu's icons and permission filter.
  const navItems = useMemo(
    () => applyNavBadges(chrome.items, countFor),
    [chrome.items, counts] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // ⚠️ THE PINNED BLOCK GOES THROUGH THE SAME PASS. Tasks declares `notify`
  // there now that it no longer appears in Work, so skipping it would leave the
  // one row people look at most as the only one with no unread badge.
  const navPinned = useMemo(
    () => applyNavBadges(chrome.pinned, countFor),
    [chrome.pinned, counts] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // ─── The Tally sync dot ──────────────────────────────────────────────────
  //
  // ⚠️ ONLY ASKED FOR BY SOMEBODY WHO CAN SEE TALLY. The endpoint is gated on
  // `nav.clients`, so fetching it for everybody would mean a 403 per page for
  // every user without that key — a permission they do not hold turned into a
  // stream of failed requests. The answer is cached across navigations (see
  // lib/tallyNavStatus.js); this layout remounts on every page.
  const canSeeTally = useMemo(
    () => canAccessNavKey(user, 'nav.clients'),
    [user]
  );
  const tallyStatus = useTallyNavStatus(canSeeTally);
  const navStatuses = useMemo(
    () => ({ tally: tallyStatusDot(tallyStatus) }),
    [tallyStatus]
  );

  // ⚠️ KEYED PER USER, not one key for the browser. One machine routinely
  // holds several accounts here — a CA is a partner at one firm and an
  // article at another — and those are not the same menu, so they must not
  // share which groups are open.
  //
  // ⚠️ ALL THREE SPELLINGS. The auth payload is read as `userId` in TeamChat,
  // `_id` in ComplianceWorkspace and `id` in EmployeeTodos — guessing one would
  // leave the key `undefined`, and the only symptom is a menu that silently
  // forgets, which nobody reports as a bug.
  const userKey = user?.userId || user?._id || user?.id;
  // ⚠️ THE `v2` IS LOAD-BEARING, NOT DECORATION. The first version shipped
  // with a default that left every group shut on the Dashboard — and the
  // persist effect then wrote that empty set to storage as though the user
  // had chosen it. Since an empty set is deliberately honoured (somebody who
  // closes every group means it), fixing the default alone would have reached
  // nobody who had already loaded the page once. Bumping the key discards a
  // preference nobody actually expressed. Bump it again if the default ever
  // changes in a way that stored state would mask.
  const navGroupStateKey = userKey ? `ds-nav-groups:v2:${userKey}` : undefined;

  // The pills. Fetched once and cached across navigations, like the Tally dot.
  const navCounts = useNavCounts(true);

  // ─── The firm row at the bottom of the menu ──────────────────────────────
  //
  // ⚠️ THE INITIALS COME FROM THE FIRM, NOT THE USER. The row names which
  // practice you are acting for — the one fact that must be unambiguous when
  // the same person holds accounts at three of them. The avatar in the top bar
  // is the person; this is the organization.
  const navFooter = useMemo(() => {
    const title = companyName || 'Your firm';
    const initials = title
      .split(/s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();
    return { title, subtitle: user?.role?.name || 'Org Admin', initials: initials || '—' };
  }, [companyName, user]);

  // Every cockpit page renders this layout, so one check here guards all of
  // them — hiding a section from the sidebar now also makes it unreachable by
  // typing its URL, which menu-gating alone never did.
  const allowed = useMemo(
    () => canAccessNavKey(user, requiredNavKeyFor(location.pathname)),
    [user, location.pathname]
  );

  // The /me payload doesn't populate the organization, so its name isn't on
  // `user`. Fetch it best-effort for the top bar; if it fails, the top bar
  // simply omits the company name rather than showing a broken value.
  useEffect(() => {
    const orgId =
      typeof user?.organizationId === 'object' ? user?.organizationId?._id : user?.organizationId;
    if (!orgId) return;
    let cancelled = false;
    axiosClient
      .get(`/organizations/${orgId}`)
      .then((res) => {
        if (!cancelled) setCompanyName(res.data?.name || '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AppLayout
      variant="orgadmin"
      brandText={chrome.brandText}
      brandIcon={chrome.brandIcon}
      items={navItems}
      onLogout={handleLogout}
      title={title}
      subtitle={subtitle}
      headerRight={headerRight}
      user={{ name: user?.name, role: user?.role?.name }}
      companyName={companyName}
      notificationCount={counts.total}
      renderNotifications={({ close }) => <NotificationPanel close={close} />}
      searchPlaceholder={chrome.searchPlaceholder}
      navStatuses={navStatuses}
      navGroupStateKey={navGroupStateKey}
      navCounts={navCounts}
      navPinned={navPinned}
      navFooter={navFooter}
      onSearch={(query) => {
        if (query) navigate(chrome.searchPath(query));
      }}
    >
      {/* ⚠️ ABOVE THE PAGE, ON EVERY PAGE, AND IT HAS NO DISMISS. It renders
          nothing at all once permission is granted, so the only way to be rid
          of it is to fix the thing it is about — which is the point: a DSC
          expiry digest that cannot reach anybody is worse than no digest,
          because the firm believes it is covered. It is silent where nothing
          can be done (an insecure origin, a browser without the API). */}
      <NotificationPermissionBanner />
      {/* ⚠️ OUTSIDE the `allowed` branch below. A partner standing on a section
          their role does not grant still has a day to mark, and hiding the
          prompt behind the permission for whatever page they happened to open
          would make it appear and disappear as they navigate. */}
      <AttendanceDayPrompt />
      {allowed ? (
        children
      ) : (
        <EmptyState
          icon="🔒"
          title="You don't have access to this section"
          description="Ask an administrator to grant it to your role from Permissions."
        />
      )}
    </AppLayout>
  );
}

export default OrgAdminLayout;
